"use client";
import React from "react";
import { motion } from "framer-motion";
import {
  FaCheckCircle,
  FaHeadphones,
  FaComments,
  FaChalkboardTeacher,
  FaPhoneAlt,
} from "react-icons/fa";

const modules = [
  "Communication Skills",
  "Debate",
  "Describing objects/situations/people",
  "Giving Directions",
  "Information Transfer",
  "Introduction to stress and Intonation",
  "Introduction to English phonetics",
  "Just a minute",
  "Listening skills",
  "Oral reporting – Oral presentation",
  "Conversation – Role play (Situational)",
  "Telephonic skills",
];

const highlights = [
  { icon: <FaHeadphones />, text: "Listening & Pronunciation Skills" },
  { icon: <FaComments />, text: "Conversation & Role-Play" },
  { icon: <FaChalkboardTeacher />, text: "Interactive Presentations" },
  { icon: <FaPhoneAlt />, text: "Telephonic & Practical Skills" },
];

const EnglishLab = () => {
  return (
    <div className="bg-white text-dark mr-50 ml-50">
      {/* Header Section */}
      <section className=" ">
        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fw-bold display-4 text-primary"
        >
          English Lab
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-3 fs-5 text-black"
        >
          The English Lab is a{" "}
          <span className="fw-semibold">LAN-based application</span> designed to
          improve <strong>language proficiency (LSRW)</strong>, pronunciation,
          and listening skills while minimizing <em>mother tongue influence</em>
          . Its modules include phonetics, conversation role-play, telephonic
          skills, and presentations.
        </motion.p>
      </section>

      {/* Highlights Section */}
      <section className=" py-5">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="fw-bold text-center text-primary mb-5"
        >
          Key Learning Highlights
        </motion.h2>

        <div className="row g-4">
          {highlights.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="col-12 col-sm-6 col-md-3"
            >
              <div className="card text-center shadow-sm h-100 border-0">
                <div className="card-body">
                  <div
                    className="d-flex justify-content-center align-items-center bg-light text-primary rounded-circle mb-3"
                    style={{ width: "60px", height: "60px", fontSize: "24px" }}
                  >
                    {item.icon}
                  </div>
                  <p className="fw-medium text-black">{item.text}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Modules Section */}
      <section className="container py-5">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="p-4 bg-light border border-primary rounded shadow-sm"
        >
          <h3 className="fw-semibold text-primary mb-4">Modules Covered</h3>
          <div className="row g-3">
            {modules.map((mod, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                viewport={{ once: true }}
                className="col-12 col-sm-6 col-md-4 mt-2"
              >
                <div className="d-flex align-items-start bg-white border rounded p-2 shadow-sm">
                  <FaCheckCircle className="text-success mt-1 me-2" />
                  <span className="text-black">{mod}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages Section */}
      <section className=" text-white py-5" style={{ backgroundColor: "#0a1530" }}>
        <div className="container text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="fw-bold mb-4"
          >
            Advantages of English Lab
          </motion.h2>
          <div className="row g-4 justify-content-center">
            {["Cost-effective", "Easy to install", "Scalable & Flexible"].map(
              (adv, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="col-12 col-sm-6 col-md-4"
                >
                  <div className="bg-white bg-opacity-10 p-3 rounded d-flex align-items-start">
                    <FaCheckCircle className="text-success mt-1 me-2" />
                    <span className="text-white" >{adv}</span>
                  </div>
                </motion.div>
              )
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnglishLab;
